/** The sticky elements a category section has to clear to be visible. */
const CATEGORY_BAR_ID = "category_bar_new";
const HEADER_ID = "_header_wrapper";
const PREVIOUS_ORDERS_BAR_ID = "previous_order_bar";
const SEARCH_BAR_ID = "search_wrapper_bar";

/** How long to keep looking for a section that has not rendered yet. */
const WAIT_TIMEOUT_MS = 2000;

// `getElementById`, not `querySelector`: an id that is not a valid CSS
// identifier makes `querySelector` throw a SyntaxError rather than return null,
// and a throw here escapes the rAF callback below — defeating the rule that
// every failure mode degrades to simply landing on the menu.
const heightOf = (id: string) =>
  document.getElementById(id)?.getBoundingClientRect().height ?? 0;

/**
 * Scrolls a menu category into view under the sticky chrome.
 *
 * A plain anchor jump — or a `#category_<id>` hash in the URL — puts the section
 * underneath the header and the category bar, which is why the category chips
 * never used one: they measure what is sticky and scroll by hand. This is that
 * measurement, shared so the chips and the promo banners cannot drift.
 *
 * [searchBarHeight] is for callers that already measured it (the chips hold it
 * in state); otherwise it is read from the DOM.
 */
export const scrollToCategorySection = ({
  anchorId,
  searchBarHeight,
}: {
  anchorId: string;
  searchBarHeight?: number;
}) => {
  const targetSection = document.getElementById(anchorId);
  if (!targetSection) {
    return false;
  }

  const targetOffset = targetSection.getBoundingClientRect().top;

  window.scrollTo({
    top:
      targetOffset +
      window.pageYOffset -
      heightOf(CATEGORY_BAR_ID) -
      heightOf(HEADER_ID) -
      heightOf(PREVIOUS_ORDERS_BAR_ID) -
      (searchBarHeight ?? heightOf(SEARCH_BAR_ID)),
    behavior: "smooth",
  });

  return true;
};

/**
 * The same scroll, for a caller that has just asked for the menu and cannot know
 * when it will be on screen — a promo banner tapped from another tab, or from
 * another page entirely.
 *
 * Retries on animation frames until the section exists, then gives up quietly:
 * landing on the menu without the scroll is still the right screen, so every
 * failure mode degrades to that rather than to an error.
 */
export const scrollToCategoryWhenReady = (categoryId: number) => {
  if (typeof window === "undefined") {
    return;
  }

  const anchorId = `category_${categoryId}`;
  const deadline = Date.now() + WAIT_TIMEOUT_MS;

  const attempt = () => {
    if (scrollToCategorySection({ anchorId })) {
      return;
    }
    if (Date.now() < deadline) {
      window.requestAnimationFrame(attempt);
    }
  };

  window.requestAnimationFrame(attempt);
};
